import { useMemo } from "react";
import { toast } from "sonner";
import { RotateCcw, ChevronDown, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStore } from "@/lib/store";
import { labelFor, labelsFor, ALL_REQUIRED_FIELDS } from "@/lib/onboardingOptions";
import api from "@/lib/api";

const STAGES = ["Pre-Seed", "Seed", "Series A", "Series B+"];
const COUNTRIES = ["US", "UK", "India", "Singapore", "Germany", "Other"];

const fmtMonthYear = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtMoney = (n) => {
  const num = Number(n) || 0;
  if (num >= 1000)
    return `$${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
  return `$${num}`;
};

export default function CompanyProfileCard() {
  const profile = useStore((s) => s.profile);
  const setProfileField = useStore((s) => s.setProfileField);
  const onboarding = useStore((s) => s.onboarding);
  const clearOnboarding = useStore((s) => s.clearOnboarding);
  const openOnboarding = useStore((s) => s.openOnboarding);

  const handleRestartOnboarding = async () => {
    if (onboarding) {
      try {
        await api.delete("/api/onboarding");
      } catch {
        /* swallow — proceed to clear locally regardless */
      }
    }
    clearOnboarding();
    openOnboarding({ then: null });
    toast.success("Onboarding reset", {
      description: "Walk through the steps again to re-personalize your boardroom.",
    });
  };

  const runwayMonths = useMemo(() => {
    const burn = Number(profile.burn) || 0;
    const mrr = Number(profile.mrr) || 0;
    const net = Math.max(burn - mrr, 1);
    // 6 months cash assumed for visual; net monthly cash burn -> months left = 6mo cash / net
    const months = Math.max(0.5, Math.min(24, (mrr * 30) / Math.max(net, 1)));
    return months.toFixed(1);
  }, [profile.burn, profile.mrr]);

  const handleSave = () =>
    toast.success("Settings saved", {
      description: "Your boardroom will use the latest profile from now on.",
    });

  const setField = (k) => (eOrVal) => {
    const v = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setProfileField(k, v);
  };

  return (
    <section
      data-testid="company-profile-card"
      className="card-soft rounded-[28px] p-6 md:p-7"
    >
      <header className="mb-5">
        <h2 className="text-[18px] font-extrabold tracking-tight text-zinc-900">
          Company Profile
        </h2>
        <p className="mt-1 text-[13px] text-zinc-500">
          Used by all agents when reasoning about your business.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_280px]">
        {/* Form */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="profile-name" className="text-[12px] font-semibold text-zinc-700">
              Company name
            </Label>
            <Input
              id="profile-name"
              data-testid="profile-name"
              value={profile.name}
              onChange={setField("name")}
              className="mt-1.5"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="profile-tagline" className="text-[12px] font-semibold text-zinc-700">
              Tagline
            </Label>
            <Input
              id="profile-tagline"
              data-testid="profile-tagline"
              value={profile.tagline}
              onChange={setField("tagline")}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-[12px] font-semibold text-zinc-700">Stage</Label>
            <Select value={profile.stage} onValueChange={(v) => setProfileField("stage", v)}>
              <SelectTrigger data-testid="profile-stage" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] font-semibold text-zinc-700">Country</Label>
            <Select value={profile.country} onValueChange={(v) => setProfileField("country", v)}>
              <SelectTrigger data-testid="profile-country" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="profile-inc" className="text-[12px] font-semibold text-zinc-700">
              Incorporation date
            </Label>
            <Input
              id="profile-inc"
              data-testid="profile-inc"
              type="date"
              value={profile.incorporationDate}
              onChange={setField("incorporationDate")}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="profile-team" className="text-[12px] font-semibold text-zinc-700">
              Team size
            </Label>
            <Input
              id="profile-team"
              data-testid="profile-team"
              type="number"
              min={1}
              value={profile.teamSize}
              onChange={(e) => setProfileField("teamSize", Number(e.target.value))}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="profile-burn" className="text-[12px] font-semibold text-zinc-700">
              Monthly burn
            </Label>
            <div className="relative mt-1.5">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400">
                $
              </span>
              <Input
                id="profile-burn"
                data-testid="profile-burn"
                type="number"
                value={profile.burn}
                onChange={(e) => setProfileField("burn", Number(e.target.value))}
                className="pl-6"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="profile-mrr" className="text-[12px] font-semibold text-zinc-700">
              MRR
            </Label>
            <div className="relative mt-1.5">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400">
                $
              </span>
              <Input
                id="profile-mrr"
                data-testid="profile-mrr"
                type="number"
                value={profile.mrr}
                onChange={(e) => setProfileField("mrr", Number(e.target.value))}
                className="pl-6"
              />
            </div>
          </div>
        </div>

        {/* Live preview */}
        <aside
          data-testid="profile-preview"
          className="rounded-2xl bg-gradient-to-b from-[#FAFAFA] to-white p-5 lg:sticky lg:top-4"
          style={{ border: "1px solid rgba(0,0,0,0.04)" }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Live preview
          </div>
          <div
            data-testid="profile-preview-name"
            className="mt-3 text-[22px] font-extrabold tracking-tight text-zinc-900"
          >
            {profile.name || "Untitled"}
          </div>
          <span className="mt-2 inline-flex rounded-full border border-black/[0.06] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-700">
            {profile.stage}
          </span>
          <p className="mt-3 text-[12px] text-zinc-500">
            {profile.tagline || "—"}
          </p>
          <div className="mt-4 text-[12px] text-zinc-600">
            {profile.country} · Incorporated {fmtMonthYear(profile.incorporationDate)}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white p-3" style={{ border: "1px solid rgba(0,0,0,0.04)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                Burn
              </div>
              <div className="mt-0.5 text-[14px] font-bold text-zinc-900">
                {fmtMoney(profile.burn)}<span className="text-[10px] font-normal text-zinc-500">/mo</span>
              </div>
            </div>
            <div className="rounded-xl bg-white p-3" style={{ border: "1px solid rgba(0,0,0,0.04)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                MRR
              </div>
              <div className="mt-0.5 text-[14px] font-bold text-zinc-900">
                {fmtMoney(profile.mrr)}<span className="text-[10px] font-normal text-zinc-500">/mo</span>
              </div>
            </div>
          </div>
          <div className="mt-3 rounded-xl bg-white p-3" style={{ border: "1px solid rgba(0,0,0,0.04)" }}>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
              Implied runway
            </div>
            <div className="mt-0.5 text-[14px] font-bold text-zinc-900">
              {runwayMonths} mo
            </div>
          </div>
        </aside>
      </div>

      <footer className="mt-6 flex items-center justify-between">
        <button
          type="button"
          data-testid="restart-onboarding-btn"
          onClick={handleRestartOnboarding}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-zinc-500 underline decoration-zinc-200 underline-offset-4 transition-colors hover:text-zinc-900 hover:decoration-zinc-900"
        >
          <RotateCcw className="h-3 w-3" strokeWidth={2.4} />
          Restart onboarding
          {onboarding && (
            <span className="ml-1 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
              · resets seeded data
            </span>
          )}
        </button>
        <button
          type="button"
          data-testid="profile-save"
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-full bg-black px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-zinc-800"
        >
          Save changes
        </button>
      </footer>

      <PhaseGSections onboarding={onboarding} openOnboarding={openOnboarding} />
    </section>
  );
}

// --- Phase G: collapsible sections surfacing the 20-question answers ------
const SECTIONS = [
  {
    id: "idea",
    title: "Idea & Vision",
    fields: [
      { key: "building", kind: "single" },
      { key: "target_customer", kind: "single" },
      { key: "core_problem", kind: "single" },
      { key: "revenue_model", kind: "single" },
    ],
  },
  {
    id: "stage",
    title: "Stage & Goal",
    fields: [
      { key: "startup_stage", kind: "single" },
      { key: "main_goal", kind: "single" },
      { key: "target_timeline", kind: "single" },
      { key: "success_90d", kind: "multi" },
    ],
  },
  {
    id: "geo",
    title: "Geography & Market",
    fields: [
      { key: "country_based", kind: "raw" },
      { key: "country_of_incorporation", kind: "raw" },
      { key: "target_market", kind: "single" },
    ],
  },
  {
    id: "budget",
    title: "Budget & Team",
    fields: [
      { key: "budget_available", kind: "single" },
      { key: "funding_source", kind: "single" },
      { key: "team_size", kind: "single" },
      { key: "founder_role", kind: "single" },
      { key: "team_skills", kind: "multi" },
      { key: "hours_per_week", kind: "single" },
    ],
  },
  {
    id: "operating",
    title: "Operating Style",
    fields: [
      { key: "operating_mode", kind: "single" },
      { key: "aggressiveness", kind: "single" },
      { key: "biggest_bottleneck", kind: "single" },
      { key: "help_needed", kind: "single" },
    ],
  },
];

const PRETTY_FIELD = {
  building: "Building",
  target_customer: "Target customer",
  core_problem: "Core problem",
  revenue_model: "Revenue model",
  startup_stage: "Stage",
  main_goal: "Main goal",
  target_timeline: "Timeline",
  success_90d: "90-day success",
  country_based: "Country",
  country_of_incorporation: "Incorporated in",
  target_market: "Target market",
  budget_available: "Budget",
  funding_source: "Funding",
  team_size: "Team size",
  founder_role: "Founder role",
  team_skills: "Skills",
  hours_per_week: "Hours / week",
  operating_mode: "Mode",
  aggressiveness: "Aggressiveness",
  biggest_bottleneck: "Bottleneck",
  help_needed: "Help needed",
};

function PhaseGSections({ onboarding, openOnboarding }) {
  const missingRequired = useMemo(() => {
    if (!onboarding) return ALL_REQUIRED_FIELDS.length;
    return ALL_REQUIRED_FIELDS.filter((f) => !onboarding[f]).length;
  }, [onboarding]);

  return (
    <div className="mt-8 border-t border-black/[0.05] pt-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-[14px] font-extrabold tracking-tight text-zinc-900">
            Boardroom context
          </h3>
          <p className="mt-0.5 text-[12px] text-zinc-500">
            Answers from the 20-question setup — the boardroom uses these on
            every debate.
          </p>
        </div>
        {missingRequired > 0 && (
          <button
            type="button"
            data-testid="complete-profile-btn"
            onClick={() => openOnboarding()}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1.5 text-[11.5px] font-semibold text-violet-700 hover:bg-violet-100"
          >
            <Sparkles className="h-3 w-3" strokeWidth={2.6} />
            Complete profile ({missingRequired} remaining)
          </button>
        )}
      </div>

      <div className="space-y-3">
        {SECTIONS.map((s) => (
          <Collapsible key={s.id} data-testid={`profile-section-${s.id}`}>
            <CollapsibleTrigger
              data-testid={`profile-section-trigger-${s.id}`}
              className="group flex w-full items-center justify-between rounded-2xl border border-black/[0.05] bg-white px-4 py-3 text-left transition-colors hover:border-black/[0.1]"
            >
              <span className="text-[13px] font-bold text-zinc-900">{s.title}</span>
              <ChevronDown
                className="h-3.5 w-3.5 text-zinc-500 transition-transform group-data-[state=open]:rotate-180"
                strokeWidth={2.4}
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 grid grid-cols-1 gap-2.5 rounded-2xl bg-zinc-50/60 px-4 py-4 sm:grid-cols-2">
                {s.fields.map((f) => (
                  <ProfileFieldChip
                    key={f.key}
                    field={f}
                    onboarding={onboarding}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  );
}

function ProfileFieldChip({ field, onboarding }) {
  const raw = onboarding?.[field.key];
  let text;
  if (field.kind === "multi") {
    const arr = raw || [];
    text = arr.length ? labelsFor(field.key, arr).join(", ") : null;
  } else if (field.kind === "raw") {
    text = raw || null;
  } else {
    text = raw ? labelFor(field.key, raw) : null;
  }
  const isSet = Boolean(text);

  return (
    <div
      data-testid={`profile-chip-${field.key}`}
      data-set={isSet ? "true" : "false"}
      className="flex items-start gap-2 rounded-xl bg-white px-3 py-2"
      style={{ border: "1px solid rgba(0,0,0,0.04)" }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          {PRETTY_FIELD[field.key] || field.key}
        </div>
        <div
          className={
            "mt-0.5 truncate text-[12.5px] font-semibold " +
            (isSet ? "text-zinc-800" : "text-zinc-400")
          }
        >
          {text || "Not set"}
        </div>
      </div>
    </div>
  );
}
